var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Modal dialog for selecting authority record type when creating a new proposal.
 * Displays a three-column grid of authority types, ordered by MARC tag.
 *
 * CZNA-456 added an optional SECOND step: once a type is picked, if that type
 * has more than one named form template, the cataloguer chooses which one to
 * start from. A type with a single template goes straight through, so the
 * common path is unchanged — the step appears only where there is a real
 * choice to make.
 *
 * The template is a property of the STARTING step only. Per the NK meeting of
 * 2026-08-04, a simplified template exists to make creation quick; review and
 * every later edit happen in the complete form, and nothing about the choice is
 * stored on the record. So this dialog is the only place a template is picked.
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, Layout, Headline, Icon, MessageBanner } from '@folio/stripes/components';
import useFormConfigTemplates, { templateDisplayName, } from '../../hooks/useFormConfigTemplates';
import { TYPE_ORDER } from './authorityTypeOrder';
import { AuthorityTypeLabel, AUTHORITY_TYPE_CONFIG } from '../AuthorityTypeCard';
// Re-exported so callers that think of this list as "what the dialog shows"
// keep their import; it LIVES in authorityTypeOrder.ts (see the note there).
export { TYPE_ORDER };
var listItemBaseStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '20px 16px',
    background: 'white',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
    textAlign: 'center',
};
var listItemHoverStyle = {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
};
// Visible keyboard focus ring — replaces the plain `outline: 'none'` above,
// which regressed keyboard accessibility (no visual indicator on Tab focus).
var listItemFocusStyle = {
    outline: '2px solid #2684ff',
    outlineOffset: '2px',
};
var TypeListItem = function (_a) {
    var type = _a.type, onClick = _a.onClick;
    var intl = useIntl();
    var _b = useState(false), hovered = _b[0], setHovered = _b[1];
    var _c = useState(false), focused = _c[0], setFocused = _c[1];
    var config = AUTHORITY_TYPE_CONFIG[type];
    var itemStyle = __assign(__assign(__assign({}, listItemBaseStyle), ((hovered || focused) ? __assign(__assign({}, listItemHoverStyle), { borderColor: config.borderColor }) : {})), (focused ? listItemFocusStyle : {}));
    return (React.createElement("button", { type: "button", style: itemStyle, onClick: onClick, onMouseEnter: function () { return setHovered(true); }, onMouseLeave: function () { return setHovered(false); }, onFocus: function () { return setFocused(true); }, onBlur: function () { return setFocused(false); } },
        React.createElement(AuthorityTypeLabel, { type: type, size: "large" }),
        React.createElement("div", { style: { fontSize: '13px', color: '#888', lineHeight: 1.4 } }, intl.formatMessage({ id: "ui-czna-authorities.proposalForm.typeDescription.".concat(type) })),
        config.marcTag !== null && (React.createElement("div", { style: { fontSize: '11px', color: '#bbb', fontFamily: 'monospace', whiteSpace: 'nowrap' } },
            "MARC ",
            config.marcTag))));
};
var templateItemStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    padding: '14px 16px',
    background: 'white',
    border: '2px solid #e8e8e8',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    textAlign: 'left',
};
var AuthorityTypeDialog = function (_a) {
    var open = _a.open, onSelect = _a.onSelect, onClose = _a.onClose, allowedTypes = _a.allowedTypes;
    var intl = useIntl();
    // The type whose template step is on screen; `null` = still on the type grid.
    var _b = useState(null), pendingType = _b[0], setPendingType = _b[1];
    // Unfiltered on purpose: the grid needs a per-type answer to "is there more
    // than one template here?" for every tile, and one request per type to decide
    // one boolean each would be the wrong trade. `isLoading` is not gated on —
    // see handleTypeClick.
    var _c = useFormConfigTemplates(), templates = _c.templates, isLoading = _c.isLoading, isError = _c.isError, refetch = _c.refetch;
    // Same string the settings selector uses (CZNA-452) — the form NK is offered
    // at creation and the form NK copies in settings must be called one thing.
    var packagedDefaultName = intl.formatMessage({
        id: 'ui-czna-authorities.settings.forms.templates.defaultName',
    });
    var templatesFor = function (type) {
        return templates.filter(function (t) { return t.recordType === type; });
    };
    // Filtered, never reordered — TYPE_ORDER stays the single say on sequence.
    var visibleTypes = allowedTypes ? TYPE_ORDER.filter(allowedTypes) : TYPE_ORDER;
    var handleTypeClick = function (type) {
        // A failed list is NOT "no templates" (CZNA-1299). Falling through to the
        // default here is how a cataloguer whose role could not read the list (403)
        // got the complete form for months while NK's templates reached only the
        // admin — and nothing on screen said so. The banner above the grid says why
        // nothing happens; the same rule `useFormConfig` follows since CZNA-1292.
        if (isError) {
            return;
        }
        // While the list is still loading we have no basis for showing a choice,
        // so we behave exactly as before CZNA-456 and let the type's default
        // template render. Blocking the grid on this request would make the common
        // single-template path slower for no gain.
        if (isLoading) {
            onSelect(type);
            return;
        }
        if (templatesFor(type).length > 1) {
            setPendingType(type);
            return;
        }
        onSelect(type);
    };
    var templateStep = pendingType !== null ? (React.createElement(Layout, { className: "padding-all-gutter" },
        React.createElement("div", { "data-testid": "authority-template-step" },
            React.createElement(Headline, { size: "medium", margin: "none" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectTemplate.heading" })),
            React.createElement("p", { style: { color: '#666', marginTop: '8px', marginBottom: '16px' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectTemplate.subtext", values: {
                        type: intl.formatMessage({
                            id: "ui-czna-authorities.recordType.".concat(pendingType),
                        }),
                    } })),
            React.createElement("div", { style: { display: 'grid', gap: '10px' } }, templatesFor(pendingType).map(function (template) { return (React.createElement("button", { key: template.templateId, type: "button", style: templateItemStyle, "data-testid": "authority-template-option-".concat(template.templateId), onClick: function () { return onSelect(pendingType, template.templateId); } },
                React.createElement("span", { style: { fontWeight: 700, fontSize: '15px', color: '#333' } }, templateDisplayName(template, intl.locale, packagedDefaultName)),
                React.createElement("span", { style: { fontSize: '12px', color: '#888', fontFamily: 'monospace' } }, template.templateId))); })),
            React.createElement("div", { style: { marginTop: '16px' } },
                React.createElement(Button, { onClick: function () { return setPendingType(null); }, "data-testid": "authority-template-back" },
                    React.createElement(Icon, { icon: "arrow-left" },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectTemplate.back" }))))))) : null;
    return (React.createElement(Modal, { open: open, onClose: onClose, label: intl.formatMessage({
            id: pendingType === null
                ? 'ui-czna-authorities.proposalForm.selectType.title'
                : 'ui-czna-authorities.proposalForm.selectTemplate.title',
        }), dismissible: true, size: "large" }, pendingType !== null ? templateStep : (React.createElement(Layout, { className: "padding-all-gutter" },
        React.createElement(Headline, { size: "medium", margin: "none" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectType.heading" })),
        React.createElement("p", { style: { color: '#666', marginTop: '8px', marginBottom: '16px' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectType.subtext" })),
        isError && (React.createElement("div", { "data-testid": "authority-templates-unavailable", style: { marginBottom: '16px' } },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.selectTemplate.unavailable" })),
            React.createElement("div", { style: { marginTop: '8px' } },
                React.createElement(Button, { onClick: refetch, "data-testid": "authority-templates-retry" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.error.boundary.retry" }))))),
        React.createElement("div", { "data-testid": "authority-type-grid", "aria-busy": isLoading, style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
            } }, visibleTypes.map(function (type) { return (React.createElement(TypeListItem, { key: type, type: type, onClick: function () { return handleTypeClick(type); } })); }))))));
};
export default AuthorityTypeDialog;
